"use client";

import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import { Check, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const CoDesignLoop = () => {
  const svgRef = useRef<SVGSVGElement>(null);
  useEffect(() => {
    if (!svgRef.current) return;
    const width = 600;
    const height = 400;
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("viewBox", `0 0 ${width} ${height}`).attr("preserveAspectRatio", "xMidYMid meet").classed("w-full h-auto max-w-lg mx-auto", true);
    const defs = svg.append("defs");
    defs.append("marker").attr("id", "arrowhead").attr("viewBox", "0 -5 10 10").attr("refX", 28).attr("refY", 0).attr("markerWidth", 6).attr("markerHeight", 6).attr("orient", "auto").append("path").attr("d", "M0,-5L10,0L0,5").attr("fill", "var(--color-chart-2)");
    const filter = defs.append("filter").attr("id", "glow");
    filter.append("feGaussianBlur").attr("stdDeviation", "2.5").attr("result", "coloredBlur");
    const feMerge = filter.append("feMerge");
    feMerge.append("feMergeNode").attr("in", "coloredBlur");
    feMerge.append("feMergeNode").attr("in", "SourceGraphic");
    const nodes = [{
      id: "ESG",
      label: "ESG Strategy",
      x: 150,
      y: 200,
      color: "var(--color-primary)"
    }, {
      id: "INST",
      label: "Institution",
      x: 450,
      y: 200,
      color: "var(--color-secondary)"
    }];
    const pathDataTop = d3.path();
    pathDataTop.moveTo(150, 160);
    pathDataTop.bezierCurveTo(150, 80, 450, 80, 450, 160);
    const pathDataBottom = d3.path();
    pathDataBottom.moveTo(450, 240);
    pathDataBottom.bezierCurveTo(450, 320, 150, 320, 150, 240);
    const pathTop = svg.append("path").attr("d", pathDataTop.toString()).attr("fill", "none").attr("stroke", "var(--color-border)").attr("stroke-width", 2).attr("stroke-dasharray", "5,5");
    const pathBottom = svg.append("path").attr("d", pathDataBottom.toString()).attr("fill", "none").attr("stroke", "var(--color-border)").attr("stroke-width", 2).attr("stroke-dasharray", "5,5");
    const particleRadius = 4;
    const particleColor = "var(--color-chart-2)";
    function animateParticle(path: d3.Selection<SVGPathElement, unknown, null, undefined>, delay: number) {
      const l = path.node()?.getTotalLength() || 0;
      const circle = svg.append("circle").attr("r", particleRadius).attr("fill", particleColor).attr("filter", "url(#glow)").attr("opacity", 0);
      circle.transition().delay(delay).duration(2000).ease(d3.easeLinear).attrTween("transform", function () {
        return function (t) {
          const p = path.node()!.getPointAtLength(t * l);
          return `translate(${p.x},${p.y})`;
        };
      }).attrTween("opacity", function () {
        return function (t) {
          return (t < 0.1 ? t * 10 : t > 0.9 ? (1 - t) * 10 : 1).toString();
        };
      }).on("end", () => {
        circle.remove();
        animateParticle(path, 0);
      });
    }
    animateParticle(pathTop, 0);
    animateParticle(pathTop, 1000);
    animateParticle(pathBottom, 0);
    animateParticle(pathBottom, 1000);
    const nodeGroups = svg.selectAll("g.node").data(nodes).join("g").attr("class", "node").attr("transform", d => `translate(${d.x},${d.y})`);
    nodeGroups.append("circle").attr("r", 50).attr("fill", "var(--color-card)").attr("stroke", "var(--color-chart-2)").attr("stroke-width", 2).attr("filter", "url(#glow)");
    nodeGroups.append("text").attr("text-anchor", "middle").attr("dy", "0.3em").attr("fill", "var(--color-foreground)").attr("font-weight", "bold").attr("font-size", "14px").text(d => d.label);
    svg.append("text").attr("x", 300).attr("y", 100).attr("text-anchor", "middle").attr("fill", "var(--color-muted-foreground)").attr("font-size", "12px").attr("font-style", "italic").text("Strategic Frameworks");
    svg.append("text").attr("x", 300).attr("y", 300).attr("text-anchor", "middle").attr("fill", "var(--color-muted-foreground)").attr("font-size", "12px").attr("font-style", "italic").text("Context & Insights");
  }, []);
  return <svg ref={svgRef} className="overflow-visible" />;
};
export default function CollaborationModel() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  const principles = [{
    title: "Embedded Capacity",
    description: "We don't just deliver strategy; we build the internal engines to sustain it."
  }, {
    title: "Co-Designed Solutions",
    description: "Your team is involved at every step, ensuring the outcome fits your unique culture."
  }, {
    title: "Mission Alignment",
    description: "Growth strategies that respect and amplify your institution's core values."
  }, {
    title: "Ownership Transfer",
    description: "Our goal is your independence. We equip you to lead the next phase without us."
  }];
  return <section id="collaboration-model" ref={sectionRef} className={`dark bg-background text-foreground py-14 lg:py-20 overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          
          {}
          <div className="relative order-2 lg:order-1">
            {}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-primary/2 blur-3xl rounded-full -z-10" />
            
            <div className="bg-card/30 border border-border/50 rounded-2xl p-8 backdrop-blur-sm shadow-2xl">
              <CoDesignLoop />
              <div className="text-center mt-6 text-sm text-muted-foreground">
                <p>Figure 1.1: The Continuous Co-Design Cycle</p>
              </div>
            </div>
          </div>

          {}
          <div className="order-1 lg:order-2 space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-foreground">
                The Co-Design Loop
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                true transformation cannot be imported; it must be cultivated from within. 
                At EduStrategy Group, we replace the traditional "expert-client" hierarchy with a 
                dynamic partnership model. We bring the frameworks; you bring the context. 
                Together, we forge a path that is uniquely yours.
              </p>
            </div>

            <div className="space-y-6">
              {principles.map((item, i) => <div key={i} data-index={i} className="flex gap-4 group">
                  <div className="mt-1 shrink-0">
                    <div className="flex items-center justify-center size-8 rounded-full bg-chart-2/10 text-chart-2 group-hover:bg-chart-2/20 transition-colors">
                      <Check className="size-5" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-foreground mb-1">
                      {item.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="pt-4">
              <Button asChild size="lg" className="bg-chart-2 hover:bg-chart-2/90 text-white border-none">
                <Link to="#">
                  Explore Our Process <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}